// Is the proef edge (apps/edge: tastings, cellar, flights, profile) reachable
// from this build? One GET /v1/health per session answers it; screens that
// depend on the edge show an honest "out of reach" state instead of a raw
// fetch error when it is not. Scanning and search never depend on it (they
// resolve on the device catalogue, then the edge, lib/api.ts), so those screens never ask. The
// health body says whether the edge reaches its R2 storage (useEdgeStorage, for About); GET
// /v1/config says which switches the operator has turned on (useEdgeFeatures, lib/features.ts).
import { EdgeConfig, HealthResponse, type HealthStorage } from "@proef/contract";
import { useEffect, useSyncExternalStore } from "react";
import { recordAiQuota } from "./aiQuota";
import { getEdgeBase, isEdgeEnabled } from "./backends";
import { getDeviceId } from "./device";

export type EdgeStatus = "unknown" | "ok" | "down";

/** The edge's storage report; "unknown" until a health body carrying one arrives. */
export interface EdgeStorage {
  readonly r2: "unknown" | HealthStorage["r2"];
  readonly slice?: HealthStorage["slice"];
}

const UNKNOWN_STORAGE: EdgeStorage = { r2: "unknown" };

/** The operator's switches as of the last config answer; everything off until one says otherwise. */
export interface EdgeFeatures {
  readonly labelScan: boolean;
}

const FEATURES_KEY = "proef.edgeFeatures";
const NO_FEATURES: EdgeFeatures = { labelScan: false };

/** The last answer survives a reload, so a flaky connection does not make a switch flap. */
function readFeatures(): EdgeFeatures {
  try {
    const raw: unknown = JSON.parse(window.localStorage.getItem(FEATURES_KEY) ?? "null");
    const parsed = EdgeConfig.safeParse({ features: raw });
    return parsed.success ? { labelScan: parsed.data.features.labelScan } : NO_FEATURES;
  } catch {
    // Blocked storage or a damaged value: off, the safe answer.
    return NO_FEATURES;
  }
}

function writeFeatures(next: EdgeFeatures): void {
  try {
    window.localStorage.setItem(FEATURES_KEY, JSON.stringify(next));
  } catch {
    // Not persisting only means the next session starts from "off" until the first answer.
  }
}

let status: EdgeStatus = "unknown";
let storage: EdgeStorage = UNKNOWN_STORAGE;
let features: EdgeFeatures = typeof window === "undefined" ? NO_FEATURES : readFeatures();
let inflight: Promise<EdgeStatus> | undefined;
const listeners = new Set<() => void>();

/**
 * Asks GET /v1/config for the switches. A network failure keeps the remembered answer, so a flaky
 * connection does not flip a switch; any answer that is not a valid config turns them off, because
 * an edge that predates the route (404) or speaks nonsense must fail closed.
 */
async function refreshFeatures(): Promise<void> {
  try {
    // The device id lets the edge report this device's label-scan allowance, not only the network's.
    const res = await fetch(`${getEdgeBase()}/v1/config`, {
      cache: "no-store",
      headers: { "x-device-id": getDeviceId() },
    });
    const body = res.ok ? EdgeConfig.safeParse(await res.json().catch(() => undefined)) : undefined;
    features = { labelScan: body?.success === true && body.data.features.labelScan };
    writeFeatures(features);
    const quota = body?.success === true ? body.data.labelScanQuota : undefined;
    if (quota !== undefined) recordAiQuota(quota);
  } catch {
    // Unreachable: the remembered answer stays.
  }
}

async function probe(): Promise<EdgeStatus> {
  let next: EdgeStatus = "down";
  let nextStorage = UNKNOWN_STORAGE;
  // Switched off in About: down without asking, so nothing leaves the device.
  if (isEdgeEnabled()) {
    try {
      const config = refreshFeatures();
      const res = await fetch(`${getEdgeBase()}/v1/health`, { cache: "no-store" });
      if (res.ok) {
        next = "ok";
        const body = HealthResponse.safeParse(await res.json().catch(() => undefined));
        if (body.success && body.data.storage !== undefined) nextStorage = body.data.storage;
      }
      await config;
    } catch {
      // Unreachable, blocked by CORS, or no address configured: all "down".
    }
  }
  // Switched off while the request was out: the answer no longer counts.
  if (!isEdgeEnabled()) {
    next = "down";
    nextStorage = UNKNOWN_STORAGE;
  }
  status = next;
  storage = nextStorage;
  listeners.forEach((l) => l());
  return next;
}

/** Probes once per session; `force` re-probes (the retry button, a changed backend setting). */
export function probeEdge(force = false): Promise<EdgeStatus> {
  if (!force && status !== "unknown") return Promise.resolve(status);
  // Cleared once settled rather than inside probe(): a switched-off probe finishes synchronously,
  // before this assignment, and would otherwise stay cached as the answer forever.
  inflight ??= probe().finally(() => {
    inflight = undefined;
  });
  return inflight;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** For code outside React (lib/learn/sync.ts flushes when the probe turns ok). */
export const subscribeEdgeStatus = subscribe;

export function getEdgeStatus(): EdgeStatus {
  return status;
}

export function useEdgeStatus(): EdgeStatus {
  useEffect(() => {
    void probeEdge();
  }, []);
  return useSyncExternalStore(
    subscribe,
    () => status,
    () => "unknown",
  );
}

export function useEdgeFeatures(): EdgeFeatures {
  useEffect(() => {
    void probeEdge();
  }, []);
  return useSyncExternalStore(
    subscribe,
    () => features,
    () => NO_FEATURES,
  );
}

export function useEdgeStorage(): EdgeStorage {
  useEffect(() => {
    void probeEdge();
  }, []);
  return useSyncExternalStore(
    subscribe,
    () => storage,
    () => UNKNOWN_STORAGE,
  );
}

/** Test seam: forget the probe result. */
export function resetEdgeStatus(): void {
  status = "unknown";
  storage = UNKNOWN_STORAGE;
  features = typeof window === "undefined" ? NO_FEATURES : readFeatures();
  inflight = undefined;
}
